import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx';

/**
 * Label + control + message. Pass a render function to receive the generated
 * id and the ids for aria-describedby, or pass `htmlFor` yourself.
 */
export function Field({
  label,
  optional,
  hint,
  error,
  htmlFor,
  className,
  labelAside,
  children,
}: {
  label?: ReactNode;
  optional?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  className?: string;
  /** Rendered on the label row, right-aligned (e.g. "Forgot password?"). */
  labelAside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={cx('min-w-0', className)}>
      {label && (
        <div className={cx(labelAside ? 'flex items-center justify-between mb-2' : '')}>
          <label htmlFor={htmlFor} className={cx('wp-label', !!labelAside && '!mb-0')}>
            {label}
            {optional && <span className="text-t5 font-normal"> · optional</span>}
          </label>
          {labelAside}
        </div>
      )}
      {children}
      {error ? (
        <div className="text-[12.5px] text-acc mt-1.5" role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className="text-[12px] text-t4 mt-1.5">{hint}</div>
      ) : null}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  mono?: boolean;
  /** 46px (default), 50px auth, 40px pill search, 36px inline edit. */
  height?: 36 | 40 | 46 | 48 | 50 | 56;
  pill?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ invalid, mono, height = 46, pill, leading, trailing, className, style, ...rest }, ref) => {
    const input = (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cx(
          'wp-field px-3.5 text-[14.5px]',
          pill && '!rounded-full',
          mono && 'font-mono',
          leading ? 'pl-[38px]' : '',
          trailing ? 'pr-12' : '',
          className,
        )}
        style={{ height, ...style }}
        {...rest}
      />
    );
    if (!leading && !trailing) return input;
    return (
      <div className="relative">
        {leading && (
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-t4 pointer-events-none flex">{leading}</span>
        )}
        {input}
        {trailing && <span className="absolute right-1.5 top-1/2 -translate-y-1/2 flex">{trailing}</span>}
      </div>
    );
  },
);
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  ({ invalid, className, rows = 3, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cx('wp-field px-3.5 py-3 text-[14.5px] leading-[1.5] resize-y', className)}
      {...rest}
    />
  ),
);
Textarea.displayName = 'Textarea';

/** Generates a stable id for wiring a label to a control. */
export const useFieldId = (prefix = 'f') => `${prefix}-${useId().replace(/:/g, '')}`;
